/*
  Wild Palace — shared desktop drag-to-scroll for carousels (FL-009).

  Adds cursor (mouse) grab-to-scroll to every WP horizontal carousel rail, so
  desktop pointer-drag works everywhere — not just touch swipe / arrows. Native
  touch scrolling is left to the browser (we ignore touch pointers).

  Design:
  - Binds to a curated list of WP rail selectors plus an opt-in [data-wp-drag].
  - Drag + the grab cursor are enabled ONLY when the rail actually overflows
    horizontally, so filled grids (e.g. the reviews grid on desktop, where the
    cards fit the row) are left completely alone. Re-checked on resize.
  - Uses Pointer Events with pointer capture (same approach as the PDP gallery),
    a small movement threshold, scroll-snap disabled mid-drag for a free follow,
    a settle-to-nearest on release, and a one-shot click suppressor so a drag
    doesn't fire the card's click.
  - Idempotent (data-wp-drag-bound) and watches for dynamically-added rails
    (recommendations, recently-viewed, section re-renders) via a MutationObserver.
*/
(function () {
  if (window.__wpDragScroll) return;
  window.__wpDragScroll = true;

  var SELECTORS = [
    '[data-wp-drag]',
    '.wild-reviews__grid',
    '.wild-multicol__grid',
    '.wild-tag-bar__list',
    '.merch-grid__track',
    '.card-grid__track',
    '.wild-ugc__track',
    '.wild-subnav__links',
    '.rv-carousel'
  ].join(',');

  var bound = [];

  function isScrollable(el) {
    var cs = getComputedStyle(el);
    if (!/(auto|scroll)/.test(cs.overflowX)) return false;
    return el.scrollWidth - el.clientWidth > 4;
  }

  function refresh(el) {
    // Toggle the grab affordance only while the rail can actually scroll.
    el.classList.toggle('wp-drag-on', isScrollable(el));
  }

  function bind(el) {
    if (!el || el.dataset.wpDragBound) return;
    el.dataset.wpDragBound = '1';
    bound.push(el);
    refresh(el);

    var down = false, moved = false, startX = 0, startScroll = 0, pid = null;

    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;       // native touch handles swipe
      if (e.button !== 0) return;                  // primary button only
      if (!isScrollable(el)) return;               // nothing to drag
      down = true; moved = false; startX = e.clientX; startScroll = el.scrollLeft; pid = e.pointerId;
    });

    el.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        el.classList.add('wp-dragging');           // disable snap -> free follow
        try { el.setPointerCapture(pid); } catch (err) {}
      }
      if (moved) { e.preventDefault(); el.scrollLeft = startScroll - dx; }
    });

    var end = function () {
      if (!down) return;
      down = false;
      el.classList.remove('wp-dragging');
      if (moved) {
        // Settle on the nearest snap position (snap class alone doesn't reliably
        // re-snap an already-scrolled position across browsers).
        var firstChild = el.children && el.children[0];
        var step = firstChild ? firstChild.getBoundingClientRect().width : el.clientWidth;
        if (step > 0) {
          var gap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || '0') || 0;
          step += gap;
          el.scrollTo({ left: Math.round(el.scrollLeft / step) * step, behavior: 'smooth' });
        }
        // Swallow the click that ends this drag so a card link doesn't fire.
        var suppress = function (ev) { ev.stopPropagation(); ev.preventDefault(); el.removeEventListener('click', suppress, true); };
        el.addEventListener('click', suppress, true);
      }
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('dragstart', function (e) { if (moved) e.preventDefault(); });
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTORS).forEach(bind);
  }

  // WS4 (19 Aug audit). The observer below used to call scan() — a whole-document
  // querySelectorAll over the 12-selector rail list, then getComputedStyle plus a
  // scrollWidth/clientWidth read (forced layout) per rail — on EVERY mutation batch that
  // added any node. On the live PDP, where the pixel manager, the tag containers, the
  // wishlist app and the customizer all inject continuously, that fires constantly:
  // isScrollable showed 68ms of self time in a CPU profile at 4x throttle, and this was a
  // meaningful share of the page's 420ms of native querySelectorAll time.
  //
  // Two changes, no behaviour lost: only rescan when an ADDED ELEMENT could actually be
  // or contain a rail (the overwhelming majority of app mutations are text nodes,
  // <script>/<iframe> tags and detached containers that can never match), and coalesce a
  // burst of batches into a single scan on the next frame instead of one scan per batch.
  var scanQueued = false;

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(function () { scanQueued = false; scan(); });
  }

  function mayContainRail(node) {
    if (!node || node.nodeType !== 1) return false;          // text/comment nodes can't match
    return (node.matches && node.matches(SELECTORS)) ||
           (node.querySelector && !!node.querySelector(SELECTORS));
  }

  function init() {
    scan();
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { bound.forEach(refresh); }, 150);
    }, { passive: true });
    // Dynamically-added rails (quick view, recently-viewed, recs re-render).
    try {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          if (!added) continue;
          for (var j = 0; j < added.length; j++) {
            if (mayContainRail(added[j])) { queueScan(); return; }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    } catch (err) {}
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
